import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, Calculator, Receipt, Truck, Car,
  Banknote, UtensilsCrossed, CarFront, Settings as SettingsIcon, Users, Shield,
  ChevronLeft, ChevronRight, LogOut, Menu, X,
  UserSquare2, Package, Briefcase, FolderOpen, Wrench, BarChart3, Video, KeySquare,
  Brain, Sparkles, Headphones, TrendingUp,
  Hammer, Brush, Heart, ClipboardCheck, CheckSquare, Building, Wine,
  Calendar as CalendarIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/AuthContext";
import { cn } from "@/lib/utils";

export const MODULE_CONFIG = {
  invoicing: { label: "Laskutus", icon: FileText, path: "/invoicing", color: "text-amber-300", hue: "from-amber-400 to-orange-500" },
  accounting: { label: "Kirjanpito", icon: Calculator, path: "/accounting", color: "text-emerald-300", hue: "from-emerald-400 to-teal-600" },
  ereceipt: { label: "E-Kuitti", icon: Receipt, path: "/receipts", color: "text-amber-200", hue: "from-yellow-400 to-amber-600" },
  crm: { label: "CRM", icon: Briefcase, path: "/crm", color: "text-indigo-300", hue: "from-indigo-400 to-purple-600" },
  hr: { label: "HR & Palkat", icon: UserSquare2, path: "/hr", color: "text-pink-300", hue: "from-pink-400 to-rose-600" },
  shifts: { label: "Työvuorot", icon: Briefcase, path: "/shifts", color: "text-emerald-300", hue: "from-emerald-400 to-green-600" },
  inventory: { label: "Varasto", icon: Package, path: "/inventory", color: "text-orange-300", hue: "from-orange-400 to-red-600" },
  projects: { label: "Projektit", icon: FolderOpen, path: "/projects", color: "text-teal-300", hue: "from-teal-400 to-cyan-600" },
  logistics: { label: "Logistiikka", icon: Truck, path: "/logistics", color: "text-purple-300", hue: "from-purple-400 to-violet-600" },
  fleet: { label: "Kalusto", icon: Wrench, path: "/fleet", color: "text-lime-300", hue: "from-lime-400 to-green-600" },
  restaurant: { label: "Ravintola", icon: UtensilsCrossed, path: "/restaurant", color: "text-rose-300", hue: "from-rose-400 to-pink-600" },
  auto_repair: { label: "Autokorjaamo", icon: Wrench, path: "/auto-repair", color: "text-orange-300", hue: "from-orange-400 to-amber-600" },
  car_rental: { label: "Autovuokraus", icon: CarFront, path: "/car-rental", color: "text-cyan-300", hue: "from-cyan-400 to-blue-600" },
  taxi: { label: "Taksi", icon: Car, path: "/taxi", color: "text-yellow-300", hue: "from-yellow-400 to-orange-600" },
  money_transfer: { label: "Rahansiirto", icon: Banknote, path: "/money-transfer", color: "text-green-300", hue: "from-green-400 to-emerald-600" },
  field_service: { label: "Kenttätyö", icon: Users, path: "/field-service", color: "text-red-300", hue: "from-red-400 to-rose-600" },
  rental: { label: "Vuokraus", icon: CarFront, path: "/rental", color: "text-blue-300", hue: "from-blue-400 to-indigo-600" },
  psa: { label: "Asiantuntijapalvelut", icon: Briefcase, path: "/psa", color: "text-purple-300", hue: "from-purple-400 to-fuchsia-600" },
  meeting: { label: "Kokoushuone", icon: Video, path: "/meeting", color: "text-cyan-300", hue: "from-cyan-400 to-sky-600" },
  reporting: { label: "Kuljetusraportointi", icon: BarChart3, path: "/reporting", color: "text-violet-400", hue: "from-violet-400 to-purple-600" },
  quotes: { label: "Tarjoukset", icon: FileText, path: "/quotes", color: "text-amber-200", hue: "from-amber-300 to-yellow-500" },

  // ===== 15 lisämoduulia (Feb 18, 2026) =====
  advanced_crm: { label: "Edistynyt CRM", icon: Briefcase, path: "/advanced-crm", color: "text-fuchsia-300", hue: "from-fuchsia-400 to-purple-600" },
  cleaning: { label: "Siivouspalvelut", icon: Brush, path: "/cleaning", color: "text-blue-300", hue: "from-blue-400 to-cyan-600" },
  construction: { label: "Rakennusprojektit", icon: Hammer, path: "/construction", color: "text-orange-300", hue: "from-orange-400 to-red-600" },
  dms: { label: "Dokumenttihallinta", icon: FileText, path: "/dms", color: "text-slate-300", hue: "from-slate-400 to-gray-600" },
  event_management: { label: "Tapahtumat", icon: CalendarIcon, path: "/events", color: "text-pink-300", hue: "from-pink-400 to-rose-600" },
  food_beverage: { label: "Catering / F&B", icon: Wine, path: "/food-beverage", color: "text-rose-300", hue: "from-rose-400 to-orange-600" },
  home_care: { label: "Kotipalvelu", icon: Heart, path: "/home-care", color: "text-rose-300", hue: "from-rose-400 to-pink-600" },
  property_management: { label: "Kiinteistöhallinta", icon: Building, path: "/property", color: "text-amber-300", hue: "from-stone-400 to-amber-600" },
  qms: { label: "Laadunhallinta", icon: ClipboardCheck, path: "/qms", color: "text-emerald-300", hue: "from-emerald-400 to-teal-600" },
  tasks: { label: "Tehtävät", icon: CheckSquare, path: "/tasks", color: "text-amber-300", hue: "from-amber-300 to-yellow-500" },
};

export default function Sidebar({ user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const userModules = user?.modules || [];
  const activeModules = userModules
    .filter((m) => MODULE_CONFIG[m])
    .map((m) => ({ key: m, ...MODULE_CONFIG[m] }));

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const isActive = (path) => (path === "/" ? location.pathname === "/" : location.pathname.startsWith(path));

  const NavRow = ({ to, Icon, label, color = "" }) => {
    const active = isActive(to);
    return (
      <Link
        to={to}
        onClick={() => setMobileOpen(false)}
        data-testid={`nav-${to.replace(/^\//, "") || "dashboard"}`}
        className={cn("nav-item group", active && "active")}
        title={collapsed ? label : undefined}
      >
        <Icon className={cn("w-[18px] h-[18px] flex-shrink-0", !active && color, active && "text-amber-200")} />
        {!collapsed && <span className="truncate">{label}</span>}
        {!collapsed && active && (
          <span className="ml-auto text-[10px] text-amber-300/70 font-mono">●</span>
        )}
      </Link>
    );
  };

  const SectionLabel = ({ children }) =>
    !collapsed && (
      <p className="px-3 pt-5 pb-2 text-[10px] uppercase tracking-[0.18em] text-white/30 font-semibold select-none">
        {children}
      </p>
    );

  const navContent = (
    <div className="flex flex-col h-full">
      {/* Brand header */}
      <div className="px-4 py-5 flex items-center gap-3 border-b border-white/5">
        <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 via-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-500/30">
          <span className="text-zinc-900 font-display font-extrabold text-sm tracking-tight">A2G</span>
          <div className="absolute -inset-0.5 rounded-xl bg-gradient-to-br from-amber-400 to-purple-600 opacity-30 blur-md -z-10" />
        </div>
        {!collapsed && (
          <div className="overflow-hidden">
            <h1 className="font-display font-bold text-base leading-tight tracking-tight text-white">
              APPS2GO <span className="text-amber-300">HUB</span>
            </h1>
            <p className="text-[10px] text-white/40 uppercase tracking-[0.25em] mt-0.5">Yritysratkaisut</p>
          </div>
        )}
      </div>

      {/* Search */}
      {!collapsed && (
        <div className="px-3 pt-4 pb-1">
          <div className="topbar-search flex items-center gap-2 px-3 py-2">
            <svg className="w-4 h-4 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Hae moduulia..."
              className="bg-transparent text-xs text-white/80 placeholder-white/30 outline-none flex-1 min-w-0"
              data-testid="sidebar-search"
            />
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5 relative z-10">
        <NavRow to="/" Icon={LayoutDashboard} label="Hallintapaneeli" />

        {activeModules.length > 0 && (
          <>
            <SectionLabel>Sovellukset</SectionLabel>
            {activeModules.map((mod) => (
              <NavRow key={mod.key} to={mod.path} Icon={mod.icon} label={mod.label} color={mod.color} />
            ))}
          </>
        )}

        <SectionLabel>Tili</SectionLabel>
        <NavRow to="/settings" Icon={SettingsIcon} label="Asetukset" />

        {(user?.role === "admin" || user?.modules?.length > 0) && (
          <>
            <SectionLabel>Liiketoiminta</SectionLabel>
            <NavRow to="/bi" Icon={Brain} label="AI Business Intelligence" color="text-violet-300" />
            <NavRow to="/partner-hub" Icon={Briefcase} label="Partner & Freelance" color="text-amber-300" />
            <NavRow to="/customer-portal" Icon={Headphones} label="Asiakasportaali" color="text-blue-300" />
            <NavRow to="/upsell-portal" Icon={Sparkles} label="Upsell-portaali" color="text-pink-300" />
            <NavRow to="/finance-reports" Icon={TrendingUp} label="Talousraportti" color="text-emerald-300" />
            <NavRow to="/staff" Icon={Users} label="Henkilökunta" color="text-cyan-300" />
            <NavRow to="/security" Icon={Shield} label="Tietoturva & Compliance" color="text-red-300" />
          </>
        )}

        {user?.role === "admin" && (
          <>
            <SectionLabel>Ylläpito</SectionLabel>
            <NavRow to="/admin/users" Icon={Users} label="Käyttäjät" color="text-amber-300" />
            <NavRow to="/admin/licenses" Icon={KeySquare} label="Lisenssit" color="text-amber-300" />
            <NavRow to="/admin/propagate" Icon={Sparkles} label="Levitä oletukset" color="text-emerald-300" />
          </>
        )}
      </nav>

      {/* User card + logout */}
      <div className="p-3 border-t border-white/5 space-y-2">
        {!collapsed && user && (
          <div className="glass-soft px-3 py-2.5 flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-purple-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {(user.full_name || user.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-white/90 truncate" data-testid="sidebar-user-name">
                {user.full_name}
              </p>
              <p className="text-[10px] text-white/40 truncate">{user.email}</p>
            </div>
          </div>
        )}
        <div className={cn("flex items-center gap-1", collapsed ? "flex-col" : "justify-between")}>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            data-testid="logout-button"
            className="text-white/50 hover:text-white hover:bg-white/5 w-8 h-8"
            title="Kirjaudu ulos"
          >
            <LogOut className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="text-white/50 hover:text-white hover:bg-white/5 w-8 h-8 hidden lg:flex"
            title={collapsed ? "Laajenna" : "Pienennä"}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile toggle */}
      <button
        onClick={() => setMobileOpen(true)}
        data-testid="mobile-nav-toggle"
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-xl bg-black/40 backdrop-blur border border-white/10 text-white/80"
      >
        <Menu className="w-5 h-5" />
      </button>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)}>
          <div className="w-72 h-full bg-[#0a0b2e]/95 backdrop-blur-2xl border-r border-white/5" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setMobileOpen(false)} className="absolute top-4 right-4 text-white/60 z-10">
              <X className="w-5 h-5" />
            </button>
            {navContent}
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside
        className={cn(
          "hidden lg:flex flex-col h-screen sticky top-0 transition-all duration-300 flex-shrink-0 z-20",
          collapsed ? "w-16" : "w-64"
        )}
        style={{
          background: "linear-gradient(180deg, rgba(10,11,46,0.92) 0%, rgba(20,10,55,0.88) 100%)",
          backdropFilter: "blur(20px)",
          borderRight: "1px solid rgba(255,255,255,0.05)",
        }}
      >
        {navContent}
      </aside>
    </>
  );
}
